import { useReadContract, useReadContracts, useWriteContract, useWaitForTransactionReceipt } from "wagmi";
import { getContractAddress, MarketFactoryABI, MarketABI } from "@/lib/contracts";

export interface MarketInfo {
  address: `0x${string}`;
  question: string;
  endTime: bigint;
  totalYesBets: bigint;
  totalNoBets: bigint;
  isResolved: boolean;
  winningOutcome: boolean;
  isOpen: boolean;
}

// Get all market addresses
export function useMarketAddresses() {
  return useReadContract({
    address: getContractAddress("MarketFactory") as `0x${string}`,
    abi: MarketFactoryABI,
    functionName: "getMarkets",
  });
}

// Get active market addresses
export function useActiveMarketAddresses() {
  return useReadContract({
    address: getContractAddress("MarketFactory") as `0x${string}`,
    abi: MarketFactoryABI,
    functionName: "getActiveMarkets",
  });
}

// Get market count
export function useMarketCount() {
  return useReadContract({
    address: getContractAddress("MarketFactory") as `0x${string}`,
    abi: MarketFactoryABI,
    functionName: "getMarketCount",
  });
}

// Get details for a single market
export function useMarketDetails(marketAddress: `0x${string}` | undefined) {
  return useReadContract({
    address: getContractAddress("MarketFactory") as `0x${string}`,
    abi: MarketFactoryABI,
    functionName: "getMarketDetails",
    args: marketAddress ? [marketAddress] : undefined,
    query: {
      enabled: !!marketAddress,
    },
  });
}

// Get market info directly from Market contract
export function useMarketInfo(marketAddress: `0x${string}` | undefined) {
  return useReadContract({
    address: marketAddress,
    abi: MarketABI,
    functionName: "getMarketInfo",
    query: {
      enabled: !!marketAddress,
    },
  });
}

// Get odds for a market
export function useMarketOdds(marketAddress: `0x${string}` | undefined) {
  return useReadContract({
    address: marketAddress,
    abi: MarketABI,
    functionName: "getOdds",
    query: {
      enabled: !!marketAddress,
    },
  });
}

// Get multiple markets with their details
export function useMarketsWithDetails(addresses: readonly `0x${string}`[] | undefined) {
  const contracts = addresses?.map((address) => ({
    address: getContractAddress("MarketFactory") as `0x${string}`,
    abi: MarketFactoryABI,
    functionName: "getMarketDetails" as const,
    args: [address] as const,
  })) || [];

  return useReadContracts({
    contracts,
    query: {
      enabled: !!addresses && addresses.length > 0,
    },
  });
}

// Combined hook to get all markets with their details
export function useAllMarkets() {
  const { data: addresses, isLoading: addressesLoading, error: addressesError } = useMarketAddresses();

  const { data: details, isLoading: detailsLoading } = useMarketsWithDetails(
    addresses as readonly `0x${string}`[] | undefined
  );

  const markets: MarketInfo[] = [];

  if (addresses && details) {
    for (let i = 0; i < addresses.length; i++) {
      const detail = details[i];
      if (detail.status === "success" && detail.result) {
        const [question, endTime, totalYesBets, totalNoBets, isResolved, winningOutcome, isOpen] = detail.result as [string, bigint, bigint, bigint, boolean, boolean, boolean];
        markets.push({
          address: addresses[i],
          question,
          endTime,
          totalYesBets,
          totalNoBets,
          isResolved,
          winningOutcome,
          isOpen,
        });
      }
    }
  }

  return {
    markets,
    isLoading: addressesLoading || detailsLoading,
    error: addressesError,
  };
}

// Get factory owner address
export function useFactoryOwner() {
  return useReadContract({
    address: getContractAddress("MarketFactory") as `0x${string}`,
    abi: MarketFactoryABI,
    functionName: "owner",
  });
}

// Resolve a market (owner only)
export function useResolveMarket() {
  const { writeContract, data: hash, isPending, error, reset } = useWriteContract();

  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash,
  });

  const resolveMarket = (marketAddress: `0x${string}`, winningOutcome: boolean) => {
    writeContract({
      address: getContractAddress("MarketFactory") as `0x${string}`,
      abi: MarketFactoryABI,
      functionName: "resolveMarket",
      args: [marketAddress, winningOutcome],
    });
  };

  return {
    resolveMarket,
    isPending: isPending || isConfirming,
    isSuccess,
    error,
    reset,
  };
}

// Close a market (owner only) - stops betting without resolving
export function useCloseMarket() {
  const { writeContract, data: hash, isPending, error, reset } = useWriteContract();

  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash,
  });

  const closeMarket = (marketAddress: `0x${string}`) => {
    writeContract({
      address: getContractAddress("MarketFactory") as `0x${string}`,
      abi: MarketFactoryABI,
      functionName: "closeMarket",
      args: [marketAddress],
    });
  };

  return {
    closeMarket,
    isPending: isPending || isConfirming,
    isSuccess,
    error,
    reset,
  };
}
